import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle, RefreshCw, Pause, Loader2 } from 'lucide-react';

type ModalMode = 'confirm' | 'active-tasks' | 'retry';

interface PauseContainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (force: boolean) => Promise<void>;
  taskCount: number; // 0 = no tasks, show simple confirmation
  isPausing: boolean;
  mode: ModalMode; // 'confirm' | 'active-tasks' | 'retry'
}

export function PauseContainerModal({
  isOpen,
  onClose,
  onConfirm,
  taskCount,
  isPausing,
  mode,
}: PauseContainerModalProps) {
  const hasActiveTasks = mode === 'active-tasks' && taskCount > 0;
  const isRetry = mode === 'retry';

  const getTitle = () => {
    if (hasActiveTasks) {
      return (
        <>
          <AlertCircle className="h-5 w-5 text-orange-500" />
          Active Tasks Detected
        </>
      );
    }
    if (isRetry) {
      return (
        <>
          <RefreshCw className="h-5 w-5 text-muted-foreground" />
          Unable to Pause
        </>
      );
    }
    return (
      <>
        <Pause className="h-5 w-5 text-muted-foreground" />
        Pause Container
      </>
    );
  };

  const getDescription = () => {
    if (hasActiveTasks) {
      return `Agent has ${taskCount} active task${taskCount > 1 ? 's' : ''} running.`;
    }
    if (isRetry) {
      return 'Would you like to try again?';
    }
    return 'Are you sure you want to pause this container?';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] w-full max-w-[90vw] rounded-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getTitle()}
          </DialogTitle>
          <DialogDescription>{getDescription()}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning Card - active tasks */}
          {hasActiveTasks && (
            <Card className="border-orange-500/20 bg-orange-50/50 dark:bg-orange-900/10">
              <CardContent className="pt-6">
                <div className="flex gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 dark:text-orange-400 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-orange-700 dark:text-orange-300">
                      Warning: Data Loss Risk
                    </p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                      Pausing now may interrupt running tasks and cause incomplete
                      changes. Consider waiting for tasks to complete.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Info for simple confirmation */}
          {mode === 'confirm' && (
            <p className="text-sm text-muted-foreground">
              The container will be paused and can be resumed later. Your code
              will be backed up.
            </p>
          )}

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose} disabled={isPausing}>
              Cancel
            </Button>
            <Button
              variant={hasActiveTasks ? 'destructive' : 'default'}
              onClick={() => onConfirm(hasActiveTasks || isRetry)} // force=true if tasks or retry
              disabled={isPausing}
            >
              {isPausing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Pausing...
                </>
              ) : hasActiveTasks ? (
                'Pause Anyway'
              ) : isRetry ? (
                'Retry'
              ) : (
                'Pause Container'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
